import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import {
    fetchRefundAudit,
    downloadCsv,
    RefundAuditResponse,
    RefundAuditRow,
} from '~/services/predefinedReports';
import { markRefundComplete } from '~/services/refund';
import { PredefinedResultsLayout, Column } from './PredefinedResultsLayout';
import RefundAuditFilterBar, {
    RefundAuditFilterValues,
} from './RefundAuditFilterBar';

type ConfigurableColumn = Column<RefundAuditRow> & {
    key: string;
    default?: boolean;
};

const money = (value: string) => `$${Number(value || 0).toFixed(2)}`;

const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
        succeeded: 'bg-success/10 text-success',
        pending: 'bg-warning/10 text-warning',
        failed: 'bg-danger/10 text-danger',
    };
    return (
        <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                colors[status] ?? 'bg-gray-100 text-gray-700'
            }`}
        >
            {status}
        </span>
    );
};

// Defaults cover the reconciliation columns finance reads on screen; the CSV
// always carries every column regardless of what is toggled here.
const ALL_COLUMNS: ConfigurableColumn[] = [
    {
        key: 'refunded_at',
        label: 'Refunded',
        default: true,
        render: (r) =>
            r.refunded_at ? moment(r.refunded_at).format('MM/DD/YYYY h:mm A') : '',
    },
    { key: 'refunded_by', label: 'Refunded By', default: true },
    { key: 'name', label: 'Donor', default: true },
    { key: 'email', label: 'Email' },
    {
        key: 'campaign_name',
        label: 'Campaign',
        default: true,
        render: (r) =>
            r.campaign_id ? (
                <Link
                    to={`/campaigns/${r.campaign_id}`}
                    className='font-bold text-primary'
                >
                    {r.campaign_name}
                </Link>
            ) : (
                r.campaign_name
            ),
    },
    { key: 'ntaf_id', label: 'NTAF ID' },
    {
        key: 'original_amount',
        label: 'Original',
        default: true,
        rightAlign: true,
        render: (r) => money(r.original_amount),
    },
    {
        key: 'donation_refunded',
        label: 'Donation Refunded',
        default: true,
        rightAlign: true,
        render: (r) => money(r.donation_refunded),
    },
    {
        key: 'tip_refunded',
        label: 'Tip Refunded',
        default: true,
        rightAlign: true,
        render: (r) => money(r.tip_refunded),
    },
    {
        key: 'total_refunded',
        label: 'Total Refunded',
        default: true,
        rightAlign: true,
        render: (r) => money(r.total_refunded),
    },
    {
        key: 'remaining_amount',
        label: 'Remaining',
        rightAlign: true,
        render: (r) => money(r.remaining_amount),
    },
    {
        key: 'is_full_refund',
        label: 'Full Refund',
        default: true,
        render: (r) => (r.is_full_refund ? 'Yes' : 'No'),
    },
    { key: 'payment_method', label: 'Payment Method' },
    {
        key: 'gift_date',
        label: 'Gift Date',
        render: (r) => (r.gift_date ? moment(r.gift_date).format('MM/DD/YYYY') : ''),
    },
    { key: 'stripe_charge_id', label: 'Stripe Charge ID' },
    { key: 'stripe_refund_id', label: 'Stripe Refund ID' },
    {
        key: 'payout_id',
        label: 'Payout ID',
        render: (r) =>
            r.payout_id ? (
                <Link
                    to={`/payouts/${r.payout_id}`}
                    className='font-bold text-primary'
                >
                    {r.payout_id}
                </Link>
            ) : (
                ''
            ),
    },
    { key: 'lookup_id', label: 'Lookup ID' },
    {
        key: 'status',
        label: 'Stripe Status',
        default: true,
        render: (r) => statusBadge(r.status),
    },
    { key: 'failure_reason', label: 'Failure Reason' },
    {
        key: 'completion_status',
        label: 'RE/MIP Entered',
        default: true,
        // Rendered in-component: the cell carries the mark-complete / reopen
        // toggle, which needs the request handler and its in-flight state.
    },
    {
        key: 'completed_at',
        label: 'Entered At',
        render: (r) =>
            r.completed_at ? moment(r.completed_at).format('MM/DD/YYYY') : '',
    },
    { key: 'completed_by', label: 'Entered By' },
    { key: 'refund_id', label: 'Refund ID' },
];

const DEFAULT_KEYS = ALL_COLUMNS.filter((c) => c.default).map((c) => c.key);
const VALID_KEYS = new Set(ALL_COLUMNS.map((c) => c.key));

const RefundAuditResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<RefundAuditResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const columnConfigRef = useRef<HTMLDivElement>(null);
    // In-flight RE/MIP toggle, keyed by refund id
    const [markingRefund, setMarkingRefund] = useState<number | null>(null);

    const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
        const fromUrl = searchParams.get('cols');
        if (fromUrl) {
            const parsed = fromUrl
                .split(',')
                .map((s) => s.trim())
                .filter((k) => VALID_KEYS.has(k));
            if (parsed.length) return parsed;
        }
        return DEFAULT_KEYS;
    });

    const fromDate = searchParams.get('from_date') || undefined;
    const toDate = searchParams.get('to_date') || undefined;
    const status = searchParams.get('status') || undefined;
    const completionStatus = searchParams.get('completion_status') || undefined;

    useEffect(() => {
        setLoading(true);
        fetchRefundAudit({
            from_date: fromDate,
            to_date: toDate,
            status,
            completion_status: completionStatus,
        })
            .then((d) => {
                setData(d);
                setError(null);
            })
            .catch((e) => {
                setError(e.message);
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [fromDate, toDate, status, completionStatus]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                columnConfigRef.current &&
                !columnConfigRef.current.contains(event.target as Node)
            ) {
                setShowColumnConfig(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // The endpoint defaults an unfiltered request to the previous calendar
    // month and reports the range it used, so fall back to the response to keep
    // the bar showing the dates the rows actually cover.
    const appliedFilters: RefundAuditFilterValues = {
        from_date: fromDate ?? data?.from_date ?? '',
        to_date: toDate ?? data?.to_date ?? '',
        status: status ?? '',
        completion_status: completionStatus ?? '',
    };

    const applyFilters = (values: RefundAuditFilterValues) => {
        const sp = new URLSearchParams(searchParams);
        (
            ['from_date', 'to_date', 'status', 'completion_status'] as const
        ).forEach((key) => {
            if (values[key]) {
                sp.set(key, values[key]);
            } else {
                sp.delete(key);
            }
        });
        // Pushed, not replaced, so Back steps through the ranges that were run.
        setSearchParams(sp);
    };

    const toggleColumn = (key: string) => {
        setVisibleColumns((prev) => {
            const next = prev.includes(key)
                ? prev.filter((k) => k !== key)
                : [...prev, key];
            const sp = new URLSearchParams(searchParams);
            const isDefault =
                next.length === DEFAULT_KEYS.length &&
                next.every((k) => DEFAULT_KEYS.includes(k));
            if (isDefault) {
                sp.delete('cols');
            } else {
                sp.set('cols', next.join(','));
            }
            setSearchParams(sp, { replace: true });
            return next;
        });
    };

    // Refunds aren't auto-linked to RE/MIP, so finance staff flip this flag by
    // hand once the entry is made (HHLTB-2586). Same action as the Payouts
    // view, offered here so a whole date range gets reviewed in one pass.
    const handleToggleCompletion = async (
        refundId: number,
        newStatus: 'complete' | 'pending',
    ) => {
        setMarkingRefund(refundId);
        try {
            const updated = await markRefundComplete(refundId, newStatus);
            // Patch the row in place rather than refetching: under a
            // completion_status filter a refetch would drop the row the moment
            // it is toggled, pulling it out from under the reviewer mid-pass.
            setData((prev) =>
                prev
                    ? {
                          ...prev,
                          rows: prev.rows.map((row) =>
                              row.refund_id === refundId
                                  ? {
                                        ...row,
                                        completion_status:
                                            updated.completion_status,
                                        completed_at: updated.completed_at,
                                        completed_by:
                                            updated.completion_status ===
                                            'complete'
                                                ? updated.completion_changed_by
                                                : '',
                                        completion_changed_by:
                                            updated.completion_changed_by,
                                        completion_changed_at:
                                            updated.completion_changed_at,
                                    }
                                  : row,
                          ),
                      }
                    : prev,
            );
            toast.success(
                newStatus === 'complete'
                    ? 'Refund marked complete'
                    : 'Refund reopened',
            );
        } catch (e) {
            toast.error(
                e instanceof Error ? e.message : 'Failed to update refund status',
            );
        } finally {
            setMarkingRefund(null);
        }
    };

    const renderCompletionCell = (r: RefundAuditRow) => {
        // A failed refund never moved money, so there is nothing to enter into
        // RE/MIP and nothing to toggle.
        if (r.status === 'failed') {
            return (
                <span className='text-gray-400' title='Refund failed — no RE/MIP entry to make'>
                    —
                </span>
            );
        }

        const isComplete = r.completion_status === 'complete';
        const saving = markingRefund === r.refund_id;
        // Reads from the audit log, so it holds a name after a reopen too. A
        // refund nobody has touched has no change to show.
        const lastChange = r.completion_changed_by
            ? [
                  isComplete ? 'Marked by' : 'Reopened by',
                  r.completion_changed_by,
                  r.completion_changed_at
                      ? `· ${moment(r.completion_changed_at).format('MM/DD/YYYY')}`
                      : '',
              ]
                  .filter(Boolean)
                  .join(' ')
            : '';

        return (
            <div className='flex flex-col items-start gap-1'>
                <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        isComplete
                            ? 'bg-success/10 text-success'
                            : 'bg-warning/10 text-warning'
                    }`}
                >
                    {isComplete ? 'Yes' : 'No'}
                </span>
                {lastChange && (
                    <span
                        className='whitespace-nowrap text-xs text-gray-500'
                        title={
                            r.completion_changed_at
                                ? moment(r.completion_changed_at).format(
                                      'MM/DD/YYYY h:mm A',
                                  )
                                : undefined
                        }
                    >
                        {lastChange}
                    </span>
                )}
                {isComplete ? (
                    <button
                        type='button'
                        onClick={() =>
                            handleToggleCompletion(r.refund_id, 'pending')
                        }
                        disabled={saving}
                        className='text-xs text-gray-500 underline transition-colors hover:text-primary disabled:opacity-50'
                    >
                        {saving ? 'Saving…' : 'Reopen'}
                    </button>
                ) : (
                    <button
                        type='button'
                        onClick={() =>
                            handleToggleCompletion(r.refund_id, 'complete')
                        }
                        disabled={saving}
                        className='inline-flex items-center rounded border border-stroke px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary disabled:opacity-50'
                    >
                        {saving ? 'Saving…' : 'Mark Complete'}
                    </button>
                )}
            </div>
        );
    };

    const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
    const columns = useMemo(
        () =>
            ALL_COLUMNS.filter((c) => visibleSet.has(c.key)).map((c) =>
                c.key === 'completion_status'
                    ? { ...c, render: renderCompletionCell }
                    : c,
            ),
        // renderCompletionCell closes over the in-flight id, so the cells have
        // to be rebuilt while a toggle is saving.
        [visibleSet, markingRefund],
    );

    const subtitle = data
        ? [
              `Refunded ${data.from_date ?? '—'} to ${data.to_date ?? '—'}`,
              `${money(data.totals.total_refunded)} refunded`,
              `${money(data.totals.donation_refunded)} donations`,
              `${money(data.totals.tip_refunded)} tips`,
          ].join(' · ')
        : undefined;

    const headerActions = (
        <div className='relative' ref={columnConfigRef}>
            <button
                type='button'
                onClick={() => setShowColumnConfig((v) => !v)}
                className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200'
                title='Configure columns'
            >
                <FaCog className='mr-2' size={14} />
                Columns
            </button>
            {showColumnConfig && (
                <div className='absolute right-0 z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg'>
                    <div className='border-b border-gray-200 p-3'>
                        <span className='text-sm font-semibold text-gray-700'>
                            Show/Hide Columns
                        </span>
                    </div>
                    <div className='max-h-72 overflow-y-auto p-2'>
                        {ALL_COLUMNS.map((column) => (
                            <label
                                key={column.key}
                                className='flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50'
                            >
                                <input
                                    type='checkbox'
                                    checked={visibleSet.has(column.key)}
                                    onChange={() => toggleColumn(column.key)}
                                    className='rounded border-gray-300 text-primary focus:ring-primary'
                                />
                                <span className='text-sm text-gray-700'>
                                    {column.label}
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className='border-t border-gray-200 p-3'>
                        <span className='text-xs text-body'>
                            The CSV always includes every column.
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <PredefinedResultsLayout
            title='Refund Audit Trail'
            subtitle={subtitle}
            columns={columns}
            rows={data?.rows ?? []}
            loading={loading}
            error={error}
            rowKey={(r) => r.refund_id}
            onDownloadCsv={() => downloadCsv('refund-audit', searchParams)}
            headerActions={headerActions}
            filters={
                <RefundAuditFilterBar
                    applied={appliedFilters}
                    onApply={applyFilters}
                    disabled={loading}
                />
            }
        />
    );
};

export default RefundAuditResults;
